<script setup lang="ts">
import { PlanetEarth, usePlanetEarthTracking } from '@ilteducation/games';
import { DialogCloseButton } from '@ilteducation/components';
import { ref, onMounted, onUnmounted } from 'vue';
import { useVideos, type Video } from '@ilteducation/videos';
import GamesBackButton from '@/components/games/GamesBackButton.vue';
import type { Poi } from '@ilteducation/games';
import poisRaw from '../assets/data/pois.geojson?raw';

interface LocationCard {
  poi: Poi;
  thumbnailLoading: boolean;
  video?: Video;
}

const videoStore = useVideos();
const { sendPoiClickedEvent, sendPoiVideoLinkClickedEvent } = usePlanetEarthTracking();

const locationCards = ref<LocationCard[]>([]);
const frontCardIndex = ref(0);

const locationStack = ref<HTMLElement | null>(null);

const getSlugFromVideoUrl = (videoUrl: string): string | undefined => {
  try {
    return new URL(videoUrl).pathname.split('/').filter(Boolean).pop();
  } catch {
    return undefined;
  }
};

const loadThumbnail = async (card: LocationCard) => {
  const slug = card.poi.videoUrl ? getSlugFromVideoUrl(card.poi.videoUrl) : undefined;
  if (!slug) {
    return;
  }

  card.thumbnailLoading = true;
  card.video = await videoStore.getVideoBySlug(slug);
  card.thumbnailLoading = false;
};

const handlePoiClicked = (pois: Poi[]) => {
  locationCards.value = pois.map((poi) => ({ poi, thumbnailLoading: false }));
  frontCardIndex.value = 0;
  locationCards.value.forEach(loadThumbnail);
};

const stackPosition = (index: number) =>
  (index - frontCardIndex.value + locationCards.value.length) % locationCards.value.length;

const handleCardClicked = (index: number) => {
  frontCardIndex.value = index;
  sendPoiClickedEvent(locationCards.value[index].poi.name);
};

const handlePoiClosed = () => {
  locationCards.value = [];
};

const handleDescriptionClick = (event: MouseEvent) => {
  const anchor = (event.target as HTMLElement).closest('a');
  if (!anchor) {
    return;
  }
  const { poi, video } = locationCards.value[frontCardIndex.value];
  sendPoiVideoLinkClickedEvent(poi.name, video?.id);
};

const handleClickOutside = (event: MouseEvent) => {
  if (!locationCards.value.length) {
    return;
  }
  const target = event.target as HTMLElement;
  if (target.closest('.poi-marker')) {
    return;
  }
  if (locationStack.value && !locationStack.value.contains(target)) {
    handlePoiClosed();
  }
};

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<template>
  <v-container fluid class="planet-earth-game pa-0">
    <v-card class="w-100 h-100 position-relative overflow-hidden" rounded="0">
      <GamesBackButton />
      <PlanetEarth :geo-json="poisRaw" @poi-clicked="handlePoiClicked" />
      <div v-if="locationCards.length" ref="locationStack" class="location-stack position-absolute">
        <v-card
          v-for="(card, index) in locationCards"
          :key="card.poi.name"
          class="location-card pa-5 rounded-lg"
          :style="{ '--stack-position': stackPosition(index), zIndex: locationCards.length - stackPosition(index) }"
        >
          <div
            class="location-card-content"
            :class="{ 'opacity-0': stackPosition(index) > 0 }"
            :inert="stackPosition(index) > 0"
          >
            <h1>{{ card.poi.name }}</h1>
            <v-skeleton-loader v-if="card.thumbnailLoading" type="image" class="mb-3 rounded" aria-hidden="true" />
            <v-img
              v-else-if="card.video?.thumbnail"
              :src="card.video.thumbnail"
              :aspect-ratio="16 / 9"
              class="mb-3 rounded"
              alt=""
            />
            <!-- The anchor tag watched for here comes from the raw v-html content; it is itself a focusable,
            keyboard-operable link, so this delegated click listener only adds analytics on top of it. -->
            <!-- eslint-disable-next-line vue/no-v-html, vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
            <div class="location-description" v-html="card.poi.description" @click="handleDescriptionClick"></div>
          </div>
          <DialogCloseButton v-if="stackPosition(index) === 0" variant="flat" size="small" @click="handlePoiClosed" />
          <v-btn
            v-else
            class="position-absolute top-0 left-0 w-100 h-100"
            variant="text"
            :aria-label="card.poi.name"
            @click="handleCardClicked(index)"
          />
        </v-card>
      </div>
    </v-card>
  </v-container>
</template>

<style scoped lang="scss">
.planet-earth-game {
  height: calc(100dvh - var(--main-top-padding) - var(--main-bottom-padding));
  max-height: calc(100dvh - var(--main-top-padding) - var(--main-bottom-padding));
}

.location-stack {
  top: min(10dvh, 10dvw);
  right: min(10dvh, 10dvw);
  width: clamp(220px, 50dvh, 500px);
  z-index: 500;
  display: grid;
}

.location-card {
  grid-area: 1 / 1;
  transform: rotate(calc(var(--stack-position) * -5deg));
  transform-origin: bottom left;
  transition: transform 400ms ease;

  h1 {
    font-size: clamp(1.25rem, 4dvh, 2rem);
  }
}

.location-card-content {
  transition: opacity 400ms ease;
}

@media (prefers-reduced-motion: reduce) {
  .location-card,
  .location-card-content {
    transition: none;
  }
}

.location-description {
  font-family: 'proxima-soft';
}
</style>
